import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, X, Eye, Type, Users, Building, Palette, Globe, Zap, History, Plus } from 'lucide-react';
import { templateService } from '../../../services/templateService';

const TemplateEditor = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    category: 'email_campaigns',
    content: '',
    subject: '',
    tags: [],
    target_audience: '',
    industry: '',
    tone: 'professional',
    language: 'en'
  });

  const [newTag, setNewTag] = useState('');
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [variables, setVariables] = useState({});
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template?.name || '',
        type: template?.type || 'email',
        category: template?.category || 'email_campaigns',
        content: template?.content || '',
        subject: template?.subject || '',
        tags: template?.tags || [],
        target_audience: template?.target_audience || '',
        industry: template?.industry || '',
        tone: template?.tone || 'professional',
        language: template?.language || 'en'
      });
      setVariables(template?.variables || {});
      
      // Load versions if editing existing template
      if (template?.id) {
        loadVersions();
      }
    }
  }, [template]);

  useEffect(() => {
    // Extract variables from content when it changes
    const extractedVars = extractVariables(formData?.content + ' ' + formData?.subject);
    setVariables(extractedVars);
  }, [formData?.content, formData?.subject]);

  const loadVersions = async () => {
    try {
      const versionsData = await templateService?.getTemplateVersions(template?.id);
      setVersions(versionsData);
    } catch (error) {
      console.error('Error loading versions:', error);
    }
  };

  const extractVariables = (text) => {
    const variables = {};
    const matches = text?.match(/\{\{(\w+)\}\}/g) || [];
    
    matches?.forEach(match => {
      const variableName = match?.replace(/\{\{|\}\}/g, '');
      variables[variableName] = 'string';
    });

    return variables;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (newTag?.trim() && !formData?.tags?.includes(newTag?.trim()?.toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev?.tags, newTag?.trim()?.toLowerCase()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async () => {
    if (!formData?.name?.trim() || !formData?.content?.trim()) {
      alert('Please fill in required fields (name and content)');
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        ...formData,
        variables,
        name: formData?.name?.trim(),
        content: formData?.content?.trim(),
        subject: formData?.subject?.trim() || null
      };

      if (template?.id) {
        await templateService?.updateTemplate(template?.id, templateData);
        
        // Create version if content changed significantly
        if (template?.content !== formData?.content) {
          await templateService?.createTemplateVersion(template?.id, {
            content: formData?.content,
            subject: formData?.subject,
            changeDescription: 'Content updated via template editor'
          });
        }
      } else {
        await templateService?.createTemplate(templateData);
      }

      onSave?.();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    let previewContent = formData?.content;
    
    // Replace variables with sample data for preview
    const sampleData = {
      firstName: 'John',
      lastName: 'Doe',
      companyName: 'Acme Corp',
      industry: formData?.industry || 'Technology',
      productName: 'Our Solution',
      senderName: 'Sarah Johnson',
      senderTitle: 'Sales Manager'
    };

    Object.entries(sampleData)?.forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      previewContent = previewContent?.replace(regex, value);
    });

    return previewContent;
  };

  const categories = [
    { value: 'email_campaigns', label: 'Email Campaigns' },
    { value: 'sms_messages', label: 'SMS Messages' },
    { value: 'social_media_posts', label: 'Social Media Posts' },
    { value: 'sales_scripts', label: 'Sales Scripts' },
    { value: 'lead_outreach', label: 'Lead Outreach' },
    { value: 'follow_up_sequences', label: 'Follow-up Sequences' },
    { value: 'promotional_content', label: 'Promotional Content' },
    { value: 'customer_support', label: 'Customer Support' }
  ];

  const tones = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'casual', label: 'Casual' },
    { value: 'formal', label: 'Formal' },
    { value: 'enthusiastic', label: 'Enthusiastic' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'empathetic', label: 'Empathetic' },
    { value: 'persuasive', label: 'Persuasive' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e?.target === e?.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              {template?.ai_generated ? (
                <Zap className="h-5 w-5 text-blue-600" />
              ) : (
                <Type className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {template ? 'Edit Template' : 'Create New Template'}
              </h2>
              <p className="text-sm text-gray-500">
                {template?.ai_generated ? 'AI Generated Template' : 'Custom Template'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {template?.id && (
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <History className="h-4 w-4" />
                Versions ({versions?.length})
              </button>
            )}
            <button
              onClick={() => setPreview(!preview)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                preview 
                  ? 'bg-blue-100 text-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Left Panel - Editor */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="p-6 overflow-y-auto flex-1">
              {/* Basic Information */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter template name..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formData?.type}
                      onChange={(e) => handleInputChange('type', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="call">Call Script</option>
                      <option value="social_media">Social Media</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData?.category}
                      onChange={(e) => handleInputChange('category', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {categories?.map((cat) => (
                        <option key={cat?.value} value={cat?.value}>
                          {cat?.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData?.type === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={formData?.subject}
                      onChange={(e) => handleInputChange('subject', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter email subject..."
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <textarea
                    value={formData?.content}
                    onChange={(e) => handleInputChange('content', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={12}
                    placeholder="Enter your template content... Use {{variableName}} for dynamic content."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use double curly braces for variables: {'{{'} firstName {'}}'}
                  </p>
                </div>

                {/* Variables */}
                {Object.keys(variables)?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detected Variables
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(variables)?.map((variable) => (
                          <span
                            key={variable}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add tag..."
                    />
                    <button
                      onClick={handleAddTag}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData?.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Additional Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Users className="h-4 w-4 inline mr-1" />
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={formData?.target_audience}
                      onChange={(e) => handleInputChange('target_audience', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., B2B decision makers"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building className="h-4 w-4 inline mr-1" />
                      Industry
                    </label>
                    <input
                      type="text"
                      value={formData?.industry}
                      onChange={(e) => handleInputChange('industry', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Palette className="h-4 w-4 inline mr-1" />
                      Tone
                    </label>
                    <select
                      value={formData?.tone}
                      onChange={(e) => handleInputChange('tone', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {tones?.map((tone) => (
                        <option key={tone?.value} value={tone?.value}>
                          {tone?.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Globe className="h-4 w-4 inline mr-1" />
                      Language
                    </label>
                    <select
                      value={formData?.language}
                      onChange={(e) => handleInputChange('language', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="it">Italian</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData?.name?.trim() || !formData?.content?.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>

          {/* Right Panel - Preview or Versions */}
          <div className="w-1/2 flex flex-col">
            {showVersions && template?.id ? (
              <div className="p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Version History</h3>
                <div className="space-y-4">
                  {versions?.map((version) => (
                    <div key={version?.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          Version {version?.version_number}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(version.created_at)?.toLocaleDateString()}
                        </span>
                      </div>
                      {version?.change_description && (
                        <p className="text-sm text-gray-600 mb-2">{version?.change_description}</p>
                      )}
                      <div className="text-xs text-gray-500">
                        By: {version?.created_by_profile?.first_name} {version?.created_by_profile?.last_name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                <div className="bg-gray-50 rounded-lg p-4 h-full">
                  {formData?.type === 'email' && formData?.subject && (
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <div className="text-sm font-medium text-gray-600 mb-1">Subject:</div>
                      <div className="font-medium">{formData?.subject}</div>
                    </div>
                  )}
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {preview ? renderPreview() : formData?.content || 'Start typing to see preview...'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TemplateEditor;